export interface RecruitBlogSection {
  heading?: string;
  body: string[];
  bullets?: string[];
}

export interface RecruitBlogPost {
  slug: string;
  title: string;
  description: string;
  publishedOn: string;
  readTime: string;
  tags: string[];
  pullQuote?: string;
  sections: RecruitBlogSection[];
}

export const recruitBlogPosts: RecruitBlogPost[] = [
  {
    slug: "why-keyword-matching-stops-working",
    title: "Why Keyword Matching Stops Working Once Everyone Uses AI",
    description:
      "When every candidate optimizes their resume with AI, the recruiter’s problem changes from filtering text to identifying real signal.",
    publishedOn: "April 2026",
    readTime: "5 min read",
    tags: ["AI sourcing", "Signal quality", "Recruiter workflow"],
    pullQuote: "The problem is no longer resume quality. The problem is signal compression.",
    sections: [
      {
        body: [
          "Most modern recruiting stacks still assume resumes are a reliable filter. That assumption breaks when candidates use the same AI tools to rewrite for the same job descriptions.",
          "Once the language converges, the recruiter no longer needs a better keyword rule. They need better evidence about communication, judgment, fit, and depth.",
        ],
      },
      {
        heading: "The recruiter job becomes evidence gathering",
        body: [
          "The best teams now ask a different question. Instead of asking whether a resume sounds strong, they ask whether the candidate has produced enough trustworthy signal across enough surfaces.",
        ],
        bullets: [
          "Resume history and role progression",
          "How they explain tradeoffs in interviews",
          "Whether their stories stay consistent across formats",
          "How quickly the hiring team can verify quality",
        ],
      },
      {
        heading: "What theRecruit changes",
        body: [
          "theRecruit is built around evidence-backed hiring. Search, ranking, spotlight intros, and AI interviews work together so the recruiter sees a fuller candidate picture before spending human time.",
        ],
      },
    ],
  },
  {
    slug: "how-to-build-a-high-signal-shortlist",
    title: "How to Build a High-Signal Shortlist Without Burning Recruiter Time",
    description:
      "A shortlist should reduce uncertainty, not just reduce volume. Here is the workflow we recommend for lean recruiting teams.",
    publishedOn: "April 2026",
    readTime: "6 min read",
    tags: ["Shortlisting", "Recruiting ops", "Hiring systems"],
    pullQuote: "A good shortlist is a decision tool, not a smaller inbox.",
    sections: [
      {
        body: [
          "Recruiters often spend too much time reducing candidate volume and too little time increasing candidate clarity. That creates shortlists that still require heavy manual rework.",
          "A better shortlist is built around confidence. Each candidate should arrive with enough context that the hiring manager can move quickly.",
        ],
      },
      {
        heading: "Use a three-layer signal model",
        body: [
          "We recommend structuring candidate evaluation in three layers so the shortlist stays rigorous and explainable.",
        ],
        bullets: [
          "Search fit: why the candidate appears in the pool",
          "Evidence fit: where the evidence supports the ranking",
          "Conversation fit: what the team should probe next",
        ],
      },
      {
        heading: "Why this matters commercially",
        body: [
          "Faster shortlists are useful, but clearer shortlists are what improve team trust, time-to-hire, and conversion rates. That is the operating metric that matters.",
        ],
      },
    ],
  },
  {
    slug: "what-evidence-backed-hiring-actually-means",
    title: "What Evidence-Backed Hiring Actually Means in Practice",
    description:
      "The phrase sounds good, but it only matters if your system makes evidence visible, legible, and comparable for the hiring team.",
    publishedOn: "March 2026",
    readTime: "4 min read",
    tags: ["Evidence-backed hiring", "AI ranking", "Recruiter experience"],
    pullQuote: "Evidence-backed hiring is only real when the recruiter can inspect the source, not just trust the score.",
    sections: [
      {
        body: [
          "Scores alone are not enough. Recruiters and hiring managers need to see where the confidence comes from and how much of it is grounded in candidate material.",
          "That is why evidence-backed hiring is a product design problem as much as a modeling problem.",
        ],
      },
      {
        heading: "Three conditions have to be true",
        body: [
          "If one of these breaks, the workflow falls back into black-box automation.",
        ],
        bullets: [
          "The signal source is explicit",
          "The score can be challenged by a human",
          "The next step in the workflow is obvious",
        ],
      },
      {
        heading: "What good looks like",
        body: [
          "The recruiter opens a candidate, sees ranking rationale, sees the evidence behind it, and immediately knows what to validate in the next conversation. That is operational leverage.",
        ],
      },
    ],
  },
];
